import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Box, TableSortable, Typography } from '@saviynt/design-system';

import useFormApiEnabled from '../../../../hooks/use-form-api-enabled';
import { toTitleCase, useInvertedLocalize } from '../../../../utils';
import { UserSkeleton } from '../../../Common';
import { type User } from '../../types';
import INITIAL_USER_LIST_COLUMNS from '../constants';
import invertedMessages from '../messages';

import UserActionMenu from './UserActionMenu/UserActionMenu';

import './UsersSortableList.css';

interface UsersSortableListProps {
  allUsers: User[];
  skeletonCount: number;
  hasMore: boolean;
  isLoadingRequestableUsers: boolean;
  isFetchingRequestableUsers: boolean;
  setLastElement: (element: HTMLDivElement | null) => void;
  offset: number;
}

function UsersSortableList({
  allUsers,
  skeletonCount,
  hasMore,
  isLoadingRequestableUsers,
  isFetchingRequestableUsers,
  setLastElement,
  offset,
}: UsersSortableListProps) {
  const { isFormApiEnabled } = useFormApiEnabled();
  const l = useInvertedLocalize(invertedMessages);

  const [columns, setColumns] = useState(INITIAL_USER_LIST_COLUMNS);

  // Add/remove action column when isFormApiEnabled changes without resetting user selections
  useEffect(() => {
    setColumns((prev) => {
      const hasActionCol = prev.some((col) => col.key === 'actionColumn');

      if (isFormApiEnabled && !hasActionCol) {
        const actionCol = INITIAL_USER_LIST_COLUMNS.find(
          (col) => col.key === 'actionColumn'
        );

        return actionCol ? [...prev, actionCol] : prev;
      }

      if (!isFormApiEnabled && hasActionCol) {
        return prev.filter((col) => col.key !== 'actionColumn');
      }

      return prev;
    });
  }, [isFormApiEnabled]);

  // Localize column labels for display without overwriting user's column selections
  const localizedColumns = useMemo(
    () =>
      columns.map((col) => ({
        ...col,
        label:
          col.key === 'actionColumn'
            ? col.label
            : l(col.label as keyof typeof invertedMessages),
      })),
    [columns, l]
  );

  const skeletonRows = Array.from(
    { length: skeletonCount },
    (_, idx) => `skeleton-${offset}-${idx}`
  ).map((key) => ({
    username: <UserSkeleton key={key} />,
    firstname: <UserSkeleton />,
    lastname: <UserSkeleton />,
    middlename: <UserSkeleton />,
    displayname: <UserSkeleton />,
    statuskey: <UserSkeleton />,
    startdate: <UserSkeleton />,
    enddate: <UserSkeleton />,
    manager: <UserSkeleton />,
    customer: <UserSkeleton />,
    employeeType: <UserSkeleton />,
    departmentname: <UserSkeleton />,
    employeeid: <UserSkeleton />,
    email: <UserSkeleton />,
    userSource: <UserSkeleton />,
    identityType: <UserSkeleton />,
    title: <UserSkeleton />,
    city: <UserSkeleton />,
    secondaryManager: <UserSkeleton />,
    location: <UserSkeleton />,
    actionColumn: <UserSkeleton />,
  }));

  const triggerRow =
    hasMore && !isLoadingRequestableUsers && !isFetchingRequestableUsers
      ? [
          {
            username: (
              <div
                ref={setLastElement}
                className='UsersSortableList-triggerRow'
              />
            ),
            firstname: '',
            lastname: '',
            middlename: '',
            displayname: '',
            statuskey: '',
            startdate: '',
            enddate: '',
            manager: '',
            customer: '',
            employeeType: '',
            departmentname: '',
            employeeid: '',
            email: '',
            userSource: '',
            identityType: '',
            title: '',
            city: '',
            secondaryManager: '',
            location: '',
            actionColumn: '',
          },
        ]
      : [];

  const getStatusLabel = (
    statuskey: string | number | null | undefined
  ): string => {
    if (statuskey === '' || statuskey == null) {
      return '-';
    }

    const STATUS_MAP: Record<number, string> = {
      1: l('Active'),
      0: l('Inactive'),
    };

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return STATUS_MAP[Number(statuskey)] ?? '-';
  };

  const rows = allUsers.map((user) => ({
    username: (
      <Box className='UsersSortableList-usernameCell'>
        <Box className='UsersSortableList-avatarWrapper'>
          <Avatar size='small' />
        </Box>
        <Box className='UsersSortableList-userInfo'>
          <Typography
            kind='body2-bold'
            color='--color-foreground-secondary-bold'
            lineLimit={1}>
            {user.firstname} {user.lastname}
          </Typography>
          <Typography
            kind='body2'
            color='--color-foreground-neutral-subtle'
            lineLimit={1}>
            {user.username}
          </Typography>
        </Box>
      </Box>
    ),
    firstname: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.firstname ?? '-'}
      </Typography>
    ),
    lastname: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.lastname ?? '-'}
      </Typography>
    ),
    middlename: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.middlename ?? '-'}
      </Typography>
    ),
    displayname: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.displayname ?? '-'}
      </Typography>
    ),
    statuskey: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {getStatusLabel(user.statuskey)}
      </Typography>
    ),
    startdate: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.startdate ?? '-'}
      </Typography>
    ),
    enddate: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.enddate ?? '-'}
      </Typography>
    ),
    manager: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.manager ?? '-'}
      </Typography>
    ),
    customer: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.customer ?? '-'}
      </Typography>
    ),
    employeeType: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument */}
        {user.employeeType ? toTitleCase(user.employeeType) : '-'}
      </Typography>
    ),
    departmentname: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.departmentname ?? '-'}
      </Typography>
    ),
    employeeid: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.employeeid ?? '-'}
      </Typography>
    ),
    email: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.email ?? '-'}
      </Typography>
    ),
    userSource: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.userSource ?? '-'}
      </Typography>
    ),
    identityType: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.identityType ?? '-'}
      </Typography>
    ),
    title: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.title ?? '-'}
      </Typography>
    ),
    city: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.city ?? '-'}
      </Typography>
    ),
    secondaryManager: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.secondaryManager ?? '-'}
      </Typography>
    ),
    location: (
      <Typography
        kind='body2'
        color='--color-foreground-secondary-bold'
        lineLimit={1}>
        {user.location ?? '-'}
      </Typography>
    ),
    actionColumn: isFormApiEnabled ? (
      <UserActionMenu userkey={user.userkey} />
    ) : null,
  }));

  return (
    <TableSortable
      data={[...rows, ...skeletonRows, ...triggerRow]}
      columns={localizedColumns}
      setColumns={setColumns}
      className='UsersSortableList-TableSortable'
      isWindowScroll={false}
      tableHeight='var(--users-list-table-height)'
    />
  );
}

export default UsersSortableList;
